// Remote-entity position interpolation (docs/multiplayer-sync/roadmap.md
// sections 2.2/3.1, Phase 2's "port Interpolator<T>'s pattern" task): buffers
// timestamped transform snapshots and samples them a short delay behind the
// local clock, so remote motion stays smooth regardless of when a snapshot
// actually arrives.
//
// Ported from sstk-sunder-web's `net/Interpolator.ts` (roadmap section 2.2:
// "already written generically enough to need only a new home in the
// engine, not a rewrite") - the *pattern*, not the code, and adapted for a
// real structural difference between the two systems: Sunder's server ticks
// and broadcasts one synchronised snapshot of every agent together each
// tick, so the original buffers one shared timeline of multi-entity frames.
// This engine has no server tick (roadmap 2.5) - "each client broadcasts
// its own transform" independently, at its own pace (roadmap 3.1: "every
// other client buffers received transforms **per source id**") - so a
// shared-frame buffer would misbehave here: two entities whose broadcasts
// never happen to land in the same buffered frame would never both appear
// in a sampled result. This version instead keeps one small timeline per
// entity id, each fed only by that entity's own arriving transforms, and
// samples every known entity's own timeline independently. It also drops
// `pitch` (this engine's `TransformSyncMessage`, syncMessage.ts, carries
// only `x/y/z/yaw` - no camera pitch is ever broadcast) and replaces the
// `@sstk/sunder-sim` `wrapAngle` import with a local one, so the engine
// stays free of a dependency on Sunder's own package (roadmap 3.3).
//
// The local player's own movement is never interpolated (roadmap 3.7's own
// non-goal list: no prediction either) - it renders straight from local
// input exactly as today. This is remote-entity rendering only.

/** The minimal transform shape the interpolator needs. A `TransformSyncMessage`
 * (syncMessage.ts) satisfies this once its `v`/`kind`/`seq` fields are
 * stripped; a compact test fixture works too. */
export interface InterpolatedTransform {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

interface Sample<T extends InterpolatedTransform> {
  time: number; // local receive-clock timeline, milliseconds
  transform: T;
}

export interface InterpolatorOptions {
  /** How far behind the latest snapshot to render, hiding broadcast spacing
   * and jitter. Must exceed the broadcast interval plus expected jitter, or
   * every single broadcast cycle produces a deterministic freeze-then-jump
   * stutter even under perfect network conditions - not just under packet
   * loss/jitter - since the sampling point would repeatedly catch up to (and
   * briefly overtake) the latest buffered sample before the next one
   * arrives. Defaults to 200ms (`DEFAULT_DELAY_MS`), which is *not* "well
   * above" every caller's own broadcast interval by construction - it is
   * only a safe margin for this engine's own ~150ms transform-broadcast
   * interval (`useMultiplayerSync.ts`'s `TRANSFORM_BROADCAST_INTERVAL_MS`).
   * A caller whose own broadcast interval differs should pass an explicit
   * `delayMs` tied to that real interval rather than relying on this
   * default silently being large enough - `useMultiplayerSync.ts` does
   * exactly that rather than trusting the two to never drift apart. */
  delayMs?: number;
  /** How much snapshot history to keep, per entity. */
  bufferMs?: number;
}

/** Matches Sunder's own default (roadmap 2.2) with a wider safety margin:
 * Sunder's server ticks/broadcasts at a fixed 50ms interval, so its own
 * 100ms default comfortably clears its broadcast interval plus jitter. This
 * engine's own default broadcast interval is 150ms
 * (`useMultiplayerSync.ts`'s `TRANSFORM_BROADCAST_INTERVAL_MS`), so
 * reusing Sunder's 100ms figure verbatim would sit *below* the broadcast
 * interval itself, not above it - see this option's own doc comment for
 * what that causes. 200ms clears 150ms plus a real jitter margin instead. */
const DEFAULT_DELAY_MS = 200;
const DEFAULT_BUFFER_MS = 1000;

/**
 * Renders remote entities (other players' own transforms, and any
 * host-driven non-player entity - roadmap 3.2) by interpolating between
 * broadcast snapshots on the local timeline, a short delay in the past, one
 * independent timeline per entity id. Smoothness is independent of when a
 * given entity's snapshot arrives: as long as the delay covers the jitter,
 * that entity's rendered motion stays continuous even with an out-of-order
 * or occasionally-dropped frame - and one entity going quiet (a paused
 * player) never affects any other entity's own sampling.
 */
export class Interpolator<T extends InterpolatedTransform> {
  private readonly timelines = new Map<string, Sample<T>[]>();
  private delayMs: number;
  private readonly bufferMs: number;

  constructor(options: InterpolatorOptions = {}) {
    this.delayMs = options.delayMs ?? DEFAULT_DELAY_MS;
    this.bufferMs = options.bufferMs ?? DEFAULT_BUFFER_MS;
  }

  /** Retune the render delay. Changing it only moves the sampling point on
   * each entity's existing timeline, so buffered samples stay valid and
   * motion stays continuous. */
  setDelayMs(delayMs: number): void {
    this.delayMs = delayMs;
  }

  get delay(): number {
    return this.delayMs;
  }

  /** Buffer one entity's transform, stamped with local-clock receive time.
   * Handles out-of-order arrivals by inserting in timeline order; a resent
   * sample at an already-buffered time replaces it rather than duplicating.
   * Entities are otherwise fully independent - adding a sample for one id
   * never touches another id's own timeline. */
  addSample(id: string, time: number, transform: T): void {
    let timeline = this.timelines.get(id);
    if (!timeline) {
      timeline = [];
      this.timelines.set(id, timeline);
    }

    let index = timeline.length;
    while (index > 0 && timeline[index - 1]!.time > time) index--;
    if (index > 0 && timeline[index - 1]!.time === time) {
      timeline[index - 1] = { time, transform };
    } else {
      timeline.splice(index, 0, { time, transform });
    }

    const cutoff = timeline[timeline.length - 1]!.time - this.bufferMs;
    while (timeline.length > 2 && timeline[0]!.time < cutoff) timeline.shift();
  }

  /** Drops an entity's entire timeline (e.g. it left relay presence) - it
   * will no longer appear in `sample()`'s results until a new one arrives. */
  remove(id: string): void {
    this.timelines.delete(id);
  }

  /** Every known entity id's own latest buffered sample time; empty when
   * nothing has been buffered for that id. */
  latestTime(id: string): number {
    const timeline = this.timelines.get(id);
    return timeline?.length ? timeline[timeline.length - 1]!.time : 0;
  }

  /** Interpolated transforms at `timeNow - delay` for every entity with at
   * least one buffered sample, where `timeNow` is the caller's own smoothly
   * advancing local clock (e.g. `performance.now()` inside a render loop) -
   * driving the render time from the caller's clock, not from snapshot
   * arrivals, is what keeps motion continuous between snapshots. Pass
   * `exclude` to omit the local player's own id, which is never
   * interpolated (it renders from local input). Order matches insertion
   * order of each id's first-ever sample, not any particular sort. */
  sample(timeNow: number, exclude?: string): Array<T & { id: string }> {
    const target = timeNow - this.delayMs;
    const out: Array<T & { id: string }> = [];
    for (const [id, timeline] of this.timelines) {
      if (id === exclude || timeline.length === 0) continue;
      out.push({ id, ...sampleTimeline(timeline, target) });
    }
    return out;
  }

  /** The same interpolation `sample()` does, for exactly one entity - for a
   * caller that already knows which id it wants (e.g. one remote-player
   * avatar component sampling only itself, every frame) and would
   * otherwise pay for iterating every other entity's timeline too. `null`
   * when this id has no buffered sample at all yet. */
  sampleOne(id: string, timeNow: number): T | null {
    const timeline = this.timelines.get(id);
    if (!timeline || timeline.length === 0) return null;
    return sampleTimeline(timeline, timeNow - this.delayMs);
  }
}

function sampleTimeline<T extends InterpolatedTransform>(timeline: Sample<T>[], target: number): T {
  const first = timeline[0]!;
  const last = timeline[timeline.length - 1]!;
  if (timeline.length === 1 || target <= first.time) return first.transform;
  if (target >= last.time) return last.transform; // not enough future yet: hold

  let a = first;
  let b = last;
  for (let i = 0; i < timeline.length - 1; i++) {
    const lo = timeline[i]!;
    const hi = timeline[i + 1]!;
    if (lo.time <= target && target <= hi.time) {
      a = lo;
      b = hi;
      break;
    }
  }

  const span = b.time - a.time;
  const t = span > 0 ? (target - a.time) / span : 0;
  return {
    ...b.transform,
    x: lerp(a.transform.x, b.transform.x, t),
    y: lerp(a.transform.y, b.transform.y, t),
    z: lerp(a.transform.z, b.transform.z, t),
    yaw: lerpAngle(a.transform.yaw, b.transform.yaw, t),
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Wraps an angle (radians) to (-π, π] - the local replacement for
 * `@sstk/sunder-sim`'s own `wrapAngle` (see this file's banner comment). */
function wrapAngle(angle: number): number {
  const twoPi = Math.PI * 2;
  let wrapped = angle % twoPi;
  if (wrapped > Math.PI) wrapped -= twoPi;
  if (wrapped <= -Math.PI) wrapped += twoPi;
  return wrapped;
}

function lerpAngle(a: number, b: number, t: number): number {
  return wrapAngle(a + wrapAngle(b - a) * t);
}
